import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalContent,
  ModalHeader,
  TableBody,
  Table,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Spinner,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import { api } from "@/lib/api-client";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import {
  type LimitationUsage,
  numberSubscriptionAppointmentUsage,
} from "./numberSubscriptionAppointmentUsage";

function formatLimitationUsage({
  timeframe,
  used,
  limitAmount,
}: LimitationUsage): string {
  const count = `${used}/${limitAmount ?? "—"}`;
  if (timeframe === "day") return t`${count} na dan`;
  if (timeframe === "week") return t`${count} na teden`;
  return count;
}

type CustomerSubscriptionAppointmentsModalProps = {
  customerSubscriptionId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const CustomerSubscriptionAppointmentsModal = ({
  customerSubscriptionId,
  isOpen,
  onClose,
}: CustomerSubscriptionAppointmentsModalProps) => {
  const [appointmentPage, setAppointmentPage] = useState(1);
  const [appointmentsPerPage, setAppointmentsPerPage] = useState(10);

  const { data } = api.customer.useGetCustomerSubscription({
    customerSubscriptionId,
    enabled: isOpen,
  });

  const {
    data: appointmentsData,
    isFetching: isFetchingAppointments,
    processedErrorMessage: appointmentsErrorMessage,
  } = api.customer.useGetCustomerSubscriptionAppointments({
    customerSubscriptionId,
    enabled: isOpen,
  });

  const subscription = data?.customerSubscription;
  const subscriptionAppointments = useMemo(
    () =>
      [...(appointmentsData?.appointments ?? [])].sort(
        (a, b) =>
          dayjs(b.appointment.startTimeUtc).valueOf() -
          dayjs(a.appointment.startTimeUtc).valueOf(),
      ),
    [appointmentsData?.appointments],
  );

  const totalAppointmentPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(subscriptionAppointments.length / appointmentsPerPage),
      ),
    [appointmentsPerPage, subscriptionAppointments.length],
  );

  useEffect(() => {
    if (isOpen) {
      setAppointmentPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (appointmentPage > totalAppointmentPages) {
      setAppointmentPage(totalAppointmentPages);
    }
  }, [appointmentPage, totalAppointmentPages]);

  const subscriptionInvoices = subscription?.invoices ?? [];
  const subscriptionLimitations = subscription?.limitations ?? [];
  const appointmentsWithPeriodUsage = useMemo(() => {
    const getPeriodLabel = (startTimeUtc: string) => {
      const appointmentTime = dayjs(startTimeUtc);
      const matchingInvoice = subscriptionInvoices
        .filter((invoice) => {
          const periodStart = dayjs(invoice.periodStart);
          const periodEnd = dayjs(invoice.periodEnd);
          return (
            appointmentTime.valueOf() >= periodStart.valueOf() &&
            appointmentTime.valueOf() <= periodEnd.valueOf()
          );
        })
        .sort(
          (invoiceA, invoiceB) =>
            dayjs(invoiceB.periodStart).valueOf() -
            dayjs(invoiceA.periodStart).valueOf(),
        )
        .at(0);

      if (!matchingInvoice) {
        return undefined;
      }

      return `${matchingInvoice.periodStartFormatted} - ${matchingInvoice.periodEndFormatted}`;
    };

    const withPeriod = subscriptionAppointments.map(
      (subscriptionAppointment) => ({
        subscriptionAppointment,
        periodLabel: getPeriodLabel(
          subscriptionAppointment.appointment.startTimeUtc.toString(),
        ),
      }),
    );

    const usages = numberSubscriptionAppointmentUsage({
      appointments: withPeriod.map(
        ({ subscriptionAppointment, periodLabel }) => ({
          startTimeUtc: subscriptionAppointment.appointment.startTimeUtc,
          countsTowardSubscriptionUsage:
            subscriptionAppointment.countsTowardSubscriptionUsage,
          periodKey: periodLabel,
        }),
      ),
      limitations: subscriptionLimitations,
    });

    return withPeriod.map((appointment, index) => ({
      ...appointment,
      usage: usages[index],
    }));
  }, [subscriptionAppointments, subscriptionInvoices, subscriptionLimitations]);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (appointmentPage - 1) * appointmentsPerPage;
    const endIndex = startIndex + appointmentsPerPage;
    return appointmentsWithPeriodUsage.slice(startIndex, endIndex);
  }, [appointmentPage, appointmentsPerPage, appointmentsWithPeriodUsage]);

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="3xl"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Seznam terminov</Trans>
        </ModalHeader>
        <ModalBody>
          {isFetchingAppointments ? (
            <div className="flex w-full justify-center py-6">
              <Spinner />
            </div>
          ) : appointmentsErrorMessage ? (
            <LimeAlert message={appointmentsErrorMessage} />
          ) : subscriptionAppointments.length ? (
            <div className="space-y-3">
              <Table isStriped removeWrapper>
                <TableHeader>
                  <TableColumn key="date">
                    <Trans>Datum</Trans>
                  </TableColumn>
                  <TableColumn key="service">
                    <Trans>Storitev</Trans>
                  </TableColumn>
                  <TableColumn key="performedBy">
                    <Trans>Opravil</Trans>
                  </TableColumn>
                  <TableColumn key="period">
                    <Trans>Obdobje</Trans>
                  </TableColumn>
                  <TableColumn key="usage">
                    <Trans>Poraba</Trans>
                  </TableColumn>
                </TableHeader>
                <TableBody>
                  {paginatedAppointments.map((appointmentWithUsage, idx) => (
                    <TableRow
                      key={`${appointmentWithUsage.subscriptionAppointment.appointment.startTimeUtc}-${idx}`}
                    >
                      <TableCell>
                        {
                          appointmentWithUsage.subscriptionAppointment
                            .appointment.startTimeFormatted
                        }
                      </TableCell>
                      <TableCell>
                        {appointmentWithUsage.subscriptionAppointment
                          .appointment.serviceName ?? t`Neznana storitev`}
                      </TableCell>
                      <TableCell>
                        {appointmentWithUsage.subscriptionAppointment
                          .appointment.userLabel ?? t`Ni podatka`}
                      </TableCell>
                      <TableCell>{appointmentWithUsage.periodLabel}</TableCell>
                      <TableCell>
                        {appointmentWithUsage.subscriptionAppointment
                          .creditType ? (
                          <Trans>Dodatni termin</Trans>
                        ) : appointmentWithUsage.usage ? (
                          formatLimitationUsage(appointmentWithUsage.usage)
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LimePagination
                className="mt-3"
                paginationProps={{
                  color: "primary",
                  page: appointmentPage,
                  total: totalAppointmentPages,
                  onChange: setAppointmentPage,
                  isCompact: true,
                }}
                perPageProps={{
                  label: t`terminov`,
                  values: [10, 20, 50],
                  value: appointmentsPerPage.toString(),
                  onChange: (value) => {
                    const parsed = Number(value);
                    if (Number.isNaN(parsed)) return;
                    setAppointmentsPerPage(parsed);
                    setAppointmentPage(1);
                  },
                }}
              />
            </div>
          ) : (
            <p className="text-default-500 text-sm">
              <Trans>Za ta paket ni porabljenih terminov.</Trans>
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            <Trans>Zapri</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
};
